import retiredVocabularyIds from "./retiredVocabularyIds.json";

// The safety net for N5/N4 vocabulary (NIH-282, kept after the NIH-281 rebuild).
//
// Why it exists. The first vocabulary build stamped a JLPT level on every dictionary
// entry that shared a kana spelling, so the N5 and N4 lists carried vulgar, violent,
// archaic and plainly wrong homophones (犯る, 殺る, 私窩子...) that a beginner,
// possibly a minor, would see. NIH-282 hid 272 of them from this module. NIH-281
// (2026-10-15) then rebuilt public/vocab-data with scripts/build-vocabulary-data.mjs
// (docs/operations/vocabulary-data.md), which keeps those entries out at the source:
// they are simply not in the data any more, and their ids are listed in
// retiredVocabularyIds.json. So HIDDEN_VOCABULARY is empty today.
//
// What is left, and still guarded. The mechanism stays as a net for whatever slips
// through a future rebuild, and as the place to record a decision to hide an id:
// - HIDDEN_VOCABULARY: ids (with level, word, reading and a reason) to hide at N5 and
//   N4. An archaic JMdict part-of-speech class is hidden at N5/N4 by rule as well.
// - REVIEWED_UNCOMMON_VOCABULARY: every N5/N4 entry marked common: false was read and
//   is kept (ordinary words the dictionary does not mark common). A new one forces a
//   human decision.
// - REVIEWED_SENSITIVE_VOCABULARY: a visible entry whose meaning matches a sensitive
//   term was read and kept.
// - FLAGGED_FOR_MANUAL_REVIEW: legitimate entries that stay visible but deserve a
//   second look. Information only; it never fails the audit.
// scripts/audit-vocabulary.js (a CI step, and its Jest twin in hiddenVocabulary.test.ts)
// fails when an N5/N4 entry breaks any of that, or when a listed row no longer matches
// the data (the id gone, or at another level, or with another word or reading).
//
// How it is applied
// - One seam: fetchVocabularyLevel (utils/vocabularyData.ts) drops hidden entries
//   from the N5 and N4 lists, so every consumer inherits the rule (Vocabulary and its
//   quiz, Verb and Adjective Conjugation, the worksheet verb picker, Speaking
//   Practice). N3, N2 and N1 are never filtered; the audit reports them.
// - Nothing is renumbered, and the JSON files are never edited by hand: they come from
//   the builder. Ids are stable, so saved progress keeps pointing at the same entries.
// - A hidden entry still resolves where something a learner owns needs it:
//   fetchFullVocabularyLevel returns the whole list, and saved-word lookup ranks a
//   hidden entry after the visible ones of the same level.
// - Retired ids (RETIRED_VOCABULARY_IDS) are ids the rebuild removed. A learner may
//   have ticked one as learned; it can no longer be shown, so it must not count
//   against VOCABULARY_TOTAL or VERB_TOTAL either. countLearnedVisible leaves them out
//   along with hidden ids, so a Home ring's "learned" never exceeds its total. XP is
//   earned, not a completion measure, and is not reduced.
// - The offline packs cache the whole JSON files, so the filter applies offline too;
//   the service worker and scripts/generate-offline-packs.js need no change.

export type HiddenVocabularyLevel = 4 | 5;

export interface ListedVocabularyEntry {
    level: HiddenVocabularyLevel;
    word: string;
    reading: string;
    reason: string;
}

// [id, level, word, reading, reason]
export type ListedVocabularyRow = readonly [string, HiddenVocabularyLevel, string, string, string];

// The levels the rule applies to.
export const HIDDEN_VOCABULARY_LEVELS: readonly number[] = [5, 4];

export function buildListedEntries(rows: readonly ListedVocabularyRow[]): ReadonlyMap<string, ListedVocabularyEntry> {
    const entries = new Map<string, ListedVocabularyEntry>();
    rows.forEach(([id, level, word, reading, reason]) => {
        if (entries.has(id)) throw new Error(`Vocabulary id ${id} is listed twice`);
        entries.set(id, { level, word, reading, reason });
    });
    return entries;
}

// Ids hidden at N5 and N4, keyed by JMdict entry id. Empty since the NIH-281 rebuild
// (see above). To hide an entry that slips through, add a row here, then update
// VOCABULARY_TOTAL and VERB_TOTAL (their tests say by how much).
export const HIDDEN_VOCABULARY: ReadonlyMap<string, ListedVocabularyEntry> = buildListedEntries([]);

// N5/N4 entries marked common: false that were read and are kept: ordinary words the
// dictionary just does not mark common. The word and reading are for the human reading
// this list and for the drift check.
export const REVIEWED_UNCOMMON_VOCABULARY: ReadonlyMap<string, ListedVocabularyEntry> = buildListedEntries([
    ["2272760", 5, "お元気ですか", "おげんきですか", "ordinary greeting: 'how are you?'. A set phrase the dictionary does not mark common"],
    ["1329015", 5, "お酒", "おさけ", "ordinary word (alcohol, sake). Genuine beginner vocabulary; see the sensitive-meaning review"],
    ["1299685", 5, "お皿", "おさら", "ordinary word: 'plate, dish'. Genuine beginner vocabulary"],
    ["2261490", 5, "おじさん", "おじさん", "ordinary word: 'uncle'. Basic family word the dictionary does not mark common"],
    ["2261500", 5, "おばさん", "おばさん", "ordinary word: 'aunt'. Basic family word the dictionary does not mark common"],
    ["1270680", 5, "御免なさい", "ごめんなさい", "ordinary apology: 'I'm sorry; my apologies; excuse me'. Core beginner phrase"],
    ["1078810", 5, "テープレコーダー", "テープレコーダー", "ordinary loanword: 'tape recorder'. Dated but still taught"],
    ["1008960", 5, "どうぞ宜しく", "どうぞよろしく", "ordinary set phrase: 'pleased to meet you; how do you do'. Core beginner greeting"],
    ["1188700", 5, "何月", "なんがつ", "ordinary question word: 'what month'. Core beginner vocabulary"],
    ["1189400", 5, "何曜日", "なんようび", "ordinary question word: 'what day of the week'. Core beginner vocabulary"],
    ["1138900", 5, "ラジオカセット", "ラジオカセット", "ordinary loanword: 'radio-cassette (player)'. Dated but still taught"],
    ["2842390", 5, "私", "わたくし", "formal first-person pronoun: 'I, me' (わたくし). Taught at beginner level"],
    ["1269350", 4, "後で", "あとで", "ordinary adverb: 'later (on); afterwards'. Everyday word"],
    ["1981450", 4, "あんなに", "あんなに", "ordinary adverb: 'to that extent; so much'. Everyday word"],
    ["1001180", 4, "お出でになる", "おいでになる", "honorific expression glossed 'to be' here (v5r). Ordinary keigo"],
    ["1001640", 4, "お陰", "おかげ", "ordinary word (お陰で 'thanks to'); the shown first sense is the religious one ('grace (of God)'), a gloss issue for NIH-283"],
    ["1604135", 4, "お祭り", "おまつり", "ordinary word: 'festival' (お祭り). Genuine beginner vocabulary"],
    ["1001870", 4, "お見舞い", "おみまい", "ordinary word: 'visit to someone ill'. Used in everyday Japanese"],
    ["2005990", 4, "泳ぎ方", "およぎかた", "ordinary compound: 'way of swimming'. Everyday word"],
    ["1004510", 4, "こちらこそ", "こちらこそ", "ordinary courtesy phrase: 'it is I who should say so'. Everyday word"],
    ["1621470", 4, "都", "と", "ordinary suffix noun: 'Metropolis (of Tokyo)', as in 東京都. Everyday word"],
    ["1282770", 4, "降り出す", "ふりだす", "ordinary verb: 'to begin to rain or snow'. Everyday word"],
    ["2015610", 4, "もうすぐ", "もうすぐ", "ordinary adverb: 'soon, shortly' (shown in kana since the rebuild, it was もう直ぐ). Everyday word"],
]);

// Entries a person read and deliberately LEFT VISIBLE, because they are legitimate
// Japanese, but that deserve a second look. Information only: the filter ignores
// this list, and the audit prints it without ever failing on it. Empty today.
export const FLAGGED_FOR_MANUAL_REVIEW: ReadonlyMap<string, ListedVocabularyEntry> = buildListedEntries([]);

// Visible N5/N4 entries whose English meaning matches a sensitive term
// (vocabularyAudit.ts SENSITIVE_MEANING_PATTERN) and that were read and kept.
// The pattern only surfaces candidates; this list is the human decision.
export const REVIEWED_SENSITIVE_VOCABULARY: ReadonlyMap<string, ListedVocabularyEntry> = buildListedEntries([
    ["1538160", 5, "薬", "くすり", "medicine; the gloss mentions '(legal) drugs'. Core beginner word"],
    ["1310730", 5, "死ぬ", "しぬ", "'to die'. Ordinary verb a learner needs"],
    ["1329015", 5, "お酒", "おさけ", "'alcohol, sake'. Ordinary beginner word"],
    ["1037670", 5, "カップ", "カップ", "'cup'; the gloss also lists 'brassiere' among its senses. Ordinary beginner word, the clothing sense is incidental"],
    ["1228260", 5, "吸う", "すう", "'to breathe in, to inhale'; the gloss also lists 'to smoke (a cigarette)'. Ordinary beginner verb"],
    ["1019280", 4, "アルコール", "アルコール", "'alcohol'. Ordinary loanword"],
    ["1390360", 4, "戦争", "せんそう", "'war'. Ordinary noun, used in history and news"],
    ["1599340", 4, "泥棒", "どろぼう", "'thief, burglar'. Ordinary noun"],
    ["1448440", 4, "盗む", "ぬすむ", "'to steal'. Ordinary verb"],
    ["1518540", 4, "亡くなる", "なくなる", "'to die, to pass away'. Polite everyday verb"],
    ["1597150", 5, "煙草", "タバコ", "'tobacco, cigarette, cigar'. Ordinary N5 vocabulary (next to 吸う and 灰皿); an adult subject but not offensive, same class as お酒"],
    ["2796520", 5, "ビール", "ビール", "'beer'. Ordinary N5 loanword, same class as お酒 and アルコール"],
    ["1567450", 4, "すり", "すり", "'pickpocket'. Ordinary noun, same class as 泥棒 (thief)"],
]);

// Ids the NIH-281 rebuild removed from public/vocab-data (written by
// scripts/build-vocabulary-data.mjs). None of them is in the data.
export const RETIRED_VOCABULARY_IDS: ReadonlySet<string> = new Set<string>(retiredVocabularyIds);

// JMdict part-of-speech classes for archaic grammar: the nidan and yodan verb
// classes (v2*, v4*), the old ku, shiku and nari adjectives, and the classical
// su verb. They are not modern Japanese and the conjugation engines cannot
// produce correct forms for them.
const ARCHAIC_PART_OF_SPEECH_PREFIX = /^(v2|v4)/;
const ARCHAIC_PART_OF_SPEECH = new Set(["adj-ku", "adj-shiku", "adj-nari", "vs-c"]);

export function isArchaicPartOfSpeech(code: string): boolean {
    return ARCHAIC_PART_OF_SPEECH_PREFIX.test(code) || ARCHAIC_PART_OF_SPEECH.has(code);
}

export interface HideableVocabularyWord {
    id: string;
    partsOfSpeech?: readonly string[];
}

export function hasArchaicPartOfSpeech(word: HideableVocabularyWord): boolean {
    return (word.partsOfSpeech ?? []).some(isArchaicPartOfSpeech);
}

export function isHiddenVocabularyLevel(level: number | string): boolean {
    return HIDDEN_VOCABULARY_LEVELS.includes(Number(level));
}

// Id-only check, for code that has progress records but not the word list (the
// Home rings). It knows only the listed ids, which is why the audit requires
// every archaic entry to be listed as well.
export function isHiddenVocabularyId(id: string, hidden: ReadonlyMap<string, unknown> = HIDDEN_VOCABULARY): boolean {
    return hidden.has(id);
}

// An id that must not count toward a completion total: hidden at N5/N4, or retired
// by the NIH-281 rebuild (no longer in the data at all). A learned flag saved on
// either stays in storage, untouched.
export function isUncountedVocabularyId(id: string, hidden: ReadonlyMap<string, unknown> = HIDDEN_VOCABULARY): boolean {
    return isHiddenVocabularyId(id, hidden) || RETIRED_VOCABULARY_IDS.has(id);
}

// The rule: at N5 and N4, an entry is hidden when its id is listed or it has an
// archaic part of speech. The hidden list is a parameter only so tests and the
// audit can run the same rule against a list of their own.
export function isHiddenVocabularyWord(
    word: HideableVocabularyWord,
    level: number | string,
    hidden: ReadonlyMap<string, unknown> = HIDDEN_VOCABULARY
): boolean {
    if (!isHiddenVocabularyLevel(level)) return false;
    return hidden.has(word.id) || hasArchaicPartOfSpeech(word);
}

export function filterVisibleVocabulary<T extends HideableVocabularyWord>(
    words: readonly T[],
    level: number | string,
    hidden: ReadonlyMap<string, unknown> = HIDDEN_VOCABULARY
): T[] {
    return words.filter((word) => !isHiddenVocabularyWord(word, level, hidden));
}

// How many ids a progress record ({ [wordId]: "learned" }) marks as learned, leaving
// out hidden and retired ids. Keeps a ring's "learned" within its total without
// loading the word lists.
export function countLearnedVisible(
    progress: Readonly<Record<string, unknown>>,
    hidden: ReadonlyMap<string, unknown> = HIDDEN_VOCABULARY
): number {
    return Object.entries(progress).filter(([id, value]) => value === "learned" && !isUncountedVocabularyId(id, hidden)).length;
}
